import {
	QueryKey,
	UseQueryOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { useEventListener, useIsomorphicLayoutEffect } from "usehooks-ts";

import { config } from "config";
import { Dictionary, first, isEmpty, isString } from "lodash";
import Papa from "papaparse";
import { estraiTestoDaPdf } from "utils/pdfReader";
import { read, utils } from "xlsx";
import { authController } from "./auth/controller";
import { runRawEmpirichQuery } from "./endpoint";
import { Fund } from "./fund/redux";
import med_to_isin from "./med_to_isin";
import { PortfolioFund } from "./portfolio/types";
import { myFormatDate } from "./simulations/PIC/fx";
import {
	FundWeight,
	Investment,
	PACResponse,
	PICResponse,
	Snap,
} from "./simulations/types";

const translateDictionary: Dictionary<string> = med_to_isin.reduce(
	(prev, next) => ({
		...prev,
		[next.PAC]: next.ISIN,
		[next.PIC]: next.ISIN,
	}),
	{},
);

export async function readXlsFile(file: File): Promise<string[][]> {
	const wb = read(await file.arrayBuffer(), {
		cellDates: true,
	});
	if (wb.SheetNames.length > 0) {
		const vals: string[][] = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
			header: 1,
		}) as any;
		return vals.map((d) => d.map((r) => (isString(r) ? r.trimEnd() : r)));
	}
	return [];
}

export function convertPortfolioFundToFundWeight(f: PortfolioFund): FundWeight {
	const isGestioneSeparata = f.fund.superCategory === "Gestioni Separate" || f.fund.nameMarket === "Gestioni Separate";
	return {
		end: f.fund.history.end,
		start:
			f.fund.history.proxy && f.proxed
				? f.fund.history.proxy
				: f.fund.history.start,
		isin: f.fund.isin,
		fund: f.fund,
		id: f.id,
		name: f.fund.name,
		weight: f.percentage,
		gsInfo: isGestioneSeparata ? f.gsInfo ? {
			id: f.id,
			retrocessione: f.gsInfo.retrocessione,
			floor: f.gsInfo.floor
		} : {
			id: f.id,
			retrocessione: 1.0,
			floor: 0.0
		} : undefined
	};
}

export function convertPortfolioToFundWeight(
	funds: PortfolioFund[],
): FundWeight[] {
	return funds.map<FundWeight>((f) => convertPortfolioFundToFundWeight(f));
}

export function convertFundToFundWeight(fund: Fund): FundWeight {
	// TO FIX.
	const history = fund.history;

	return {
		isin: fund.isin,
		id: fund.id,
		name: fund.name,
		fund: fund,
		start: history.proxy ? history.proxy : history.start,
		end: history.end,
		weight: 0,
	};
}

export function maxNumber(nums: number[]): number {
	if (nums.length === 0) return 0; // throw new Error("devi passarmi un array di numeri con almeno un elemento")
	const result = nums.sort((p, q) => q - p);
	return result[0];
}

export function minNumber(nums: number[]): number {
	if (nums.length === 0) return 0; // throw new Error("devi passarmi un array di numeri con almeno un elemento")
	const result = nums.sort((p, q) => p - q);
	return result[0];
}

export function convertFundToPortfolioFund(
	fund: Fund,
	shouldEnableProxy: boolean = true,
): PortfolioFund {
	const isGestioneSeparata = fund.superCategory === "Gestioni Separate" || fund.nameMarket === "Gestioni Separate";
	return {
		fund,
		id: fund.id,
		quote: 0,
		money: fund.amount || 0,
		percentage: 0,
		proxed:
			fund.history.proxy !== undefined &&
				fund.history.proxy !== 0 &&
				shouldEnableProxy
				? true
				: false,
		gsInfo: isGestioneSeparata ? {
			id: fund.id,
			retrocessione: 1.0,
			floor: 0.0
		} : undefined,
	};
}

export function isIsinOrTicker(value: string): boolean {
	if (!value) return false;
	return importIsinString(value).length === 1 || importTickerString(value).length === 1;
}

export function isIsin(value: string): boolean {
	if (!value) return false;
	return importIsinString(value).length === 1;
}

export function importTickerString(isins: string): string[] {
	//FR0000133308
	const result: string[] = [];
	for (let i of isins.matchAll(/[a-zA-Z-0-9_$€£]{5,9}/g)) {
		//console.log(i)
		result.push(i[0]);
	}
	return result;
}

export function translateMedCodeToIsin(value?: string): string {
	// console.log("translateMedCodeToIsin: ", value, value ?? "undefined" in translateDictionary)
	// if (value != null && value.length === 3 && value in translateDictionary) {
	if ((value ?? "").trim().length <= 12) {
		if (value != null && value in translateDictionary) {
			return translateDictionary[value];
		} else {
			return (value ?? "");
		}
	} else return translateMedCodeToIsin((value ?? "").trim().slice(-12));
}
export function translateMedCodeToIsins(value: string[]): string[] {
	return value.map((d) => {
		if (d in translateDictionary) {
			return translateDictionary[d];
		} else {
			return d;
		}
	});
}

export function importIsinString(isins: string): string[] {
	//FR0000133308
	// console.log('Match', isins)
	const result: string[] = [];
	for (let i of isins.matchAll(/[A-Z]{2}[A-Z0-9]{9}[0-9]{1}/g)) {
		const isin = i[0].trimEnd();
		if (isin.length <= 12) {
			result.push(isin);
		}
	}
	return result;
}

function parseAmount(amount: any): any {
	if (typeof amount === "number") {
		return amount;
	}
	// First let's remove any currency sign.
	return parseFloat(
		(amount || "0")
			.replace(/$|€|£|¥/gi, "")
			.replace(/\./g, "")
			.replace(/,/g, ".")
			.trimEnd(),
	);
}

export interface ParsedFund {
	isin: string;
	amount: number;
	quote: number;
}

const allowedFirstColumn = [
	"DESCRIZIONE",
	"COMPOSIZIONE",
]

const allowedSecondColumn = [
	"ISIN",
	"TITOLO ILLIQUIDO",
]

export function parseMediolanumInput(value: string): ParsedFund[] {
	const allowedColumnsCombinations: string[] = [];
	allowedFirstColumn.forEach((first) => {
		allowedSecondColumn.forEach((second) => {
			allowedColumnsCombinations.push(`${first}\t${second}`);
		});
	});
	const startCutIndexes = allowedColumnsCombinations.map(a => value.indexOf(a)).filter(a => a >= 0);
	if (startCutIndexes.length <= 0) {
		throw new Error("Cannot find isin or amount colums");
	}
	// endIndex to be deleted when certificates will be in our database
	// const endIndex = value.indexOf("CERTIFICATES") >= 0 ? value.indexOf("CERTIFICATES") : value.length;
	// Then trim the start and the end to avoid lines misleading.
	// const input: any = fixMediolanumFormat(value.slice(Math.min(...startCutIndexes), endIndex));
	const input: any = fixMediolanumFormat(value.slice(Math.min(...startCutIndexes)));
	// console.log("FIXED MED RESULT", input)
	const result: any = Papa.parse(input, {
		delimiter: "\t",
		header: true,
	});
	// console.log("PMI RESULT cols: ", result.meta.fields);
	// console.log(result.data);

	// Get the two isin and amount columns.

	const isinColumn: any = result.meta.fields.find(
		(c: any) => c.indexOf("ISIN") >= 0 || c.indexOf("TITOLO ILLIQUIDO") >= 0,
	);
	const amountColumn: any = result.meta.fields.find(
		(c: any) => c.indexOf("CONTROVALORE") >= 0,
	);

	const quoteColumn: any = result.meta.fields.find(
		(c: any) => c.indexOf("SALDO QUOTE") >= 0,
	);

	// console.log(`Isin col: ${isinColumn}, CV col: ${amountColumn}, QT col: ${quoteColumn}`);

	// Safe check.
	if (!isinColumn || !amountColumn) {
		throw new Error("Cannot find isin or amount colums");
	}
	// Let's get all the values we need.
	const parsedResults = result.data.map((f: any) => {
		const isinInName = (f[isinColumn] ?? "").length > 12;
		let rawAmount = 0;
		if (isinInName === true) {
			// if isin in name we have to shift amount column by one
			const amountColIndex = result.meta.fields.indexOf(amountColumn)
			rawAmount = f[result.meta.fields[amountColIndex + 1]];
		} else rawAmount = f[amountColumn];

		return {
			isin: translateMedCodeToIsin(f[isinColumn]),
			amount: parseAmount(rawAmount),
			quote: parseAmount(f[quoteColumn]),
		}
	});

	// Now we have to check if the fund is available.
	return (parsedResults as ParsedFund[]).filter((e) => isIsinOrTicker(e.isin));
}

export function parseFinecoInput(input: any): ParsedFund[] {
	const text = input
		.replace("Peso Fondi/Sicav\n/ETF/ETC", "Peso Fondi/Sicav/ETF/ETC")
		.replace("Ranking\nAdvice", "Ranking Advice");
	// console.log(input);
	// console.log(text);
	const result: any = Papa.parse(text, {
		delimiter: "\t",
		header: true,
	});

	// Get the two isin and amount columns.
	const isinColumn: any = result.meta.fields.find(
		(c: any) => c.toLowerCase().indexOf("isin") >= 0,
	);
	const amountColumn: any = result.meta.fields.find(
		(c: any) => c.toLowerCase().indexOf("controvalore") >= 0,
	);
	// Let's get all the values we need.
	const parsedResults = result.data.map((f: any) => ({
		isin: f[isinColumn],
		amount: parseAmount(f[amountColumn]),
	}));

	// Now we have to check if the fund is available.
	return (parsedResults as ParsedFund[]).filter((e) => isIsinOrTicker(e.isin));
}

export function parseBNLInput(input: any): ParsedFund[] {
	const text = input;
	// console.log(input);
	// console.log(text);
	const result: any = Papa.parse(text, {
		delimiter: "\t",
	});
	// console.log(result);

	return [];
}

export function fixMediolanumFormat(input: any) {
	return input
		.split(/\r?\n/)
		.filter((p: any) => p.indexOf("▼") < 0 && p.indexOf("►") < 0)
		.reduce((prev: any, next: any, index: any, arr: any) => {
			// Only on 0 or odd indexes.
			if (index % 2 === 0) {
				return [...prev, next + "\t" + arr[index + 1]];
			} else {
				return prev;
			}
		}, [])
		.map(function (l: any) {
			return l.trimStart();
		})
		.join("\n")
		.trimStart()
		.trimEnd()
		.replaceAll(/Totale(.)*\n(.)*\n(.)*MWRR/g, "")
		.split(/\r?\n/)
		.map((d: string) => {
			const cols = d.split(/\t/);
			const firstCol = first(cols) ?? "";
			if (firstCol.match(/([A-Z]{2})([A-Z0-9]{9})([0-9]{1})/g)) {
				return ` \t${d}`;
			} else {
				return d;
			}
		})
		.join("\n");
}

export function importFromExcelFile(rows: string | number[][]): ParsedFund[] {
	const result: ParsedFund[] = [];
	let isQuote = false;
	let isBanco = false;
	for (let row of rows) {
		// console.log(row[0], row[1]);
		if (!row[0] || !row[1]) continue;
		const isin = translateMedCodeToIsin(row[0].toString().toUpperCase());
		const amount = row[1].toString().toUpperCase().replace(",", ".");
		if (isBanco) {
			result.push({
				isin: row[2].toString().toUpperCase(),
				amount: parseFloat(row[12].toString().toUpperCase()),
				quote: parseFloat(row[6].toString().toUpperCase()),
			});
		}
		if (isin === "HORIZONTE TEMPORAL") {
			isBanco = true;
		}
		if (amount === "QUOTE") {
			// console.log("Is quote!");
			isQuote = true;
		}
		// console.log("import excel result");
		// console.log(row[0].toString().toUpperCase(), isin, isISIN(isin), parseFloat(amount), isQuote, isBanco);
		if (isIsinOrTicker(isin)) {
			result.push({
				isin,
				amount: isQuote ? 0 : parseFloat(amount) || 0,
				quote: isQuote ? parseFloat(amount) || 0 : 0,
			});
		}
	}
	// console.log("import excel result", result);
	return result;
}

export function getDays(start: Date, end: Date): number {
	if (start.getTime() > end.getTime())
		throw new Error("non puoi passarmi lo start superiore all'end");

	let hours = Math.abs((start.getTime() - end.getTime()) / 1000 / 60 / 60);
	let days = Math.ceil(hours / 24);
	// console.log("Get days", start.toISOString(), end.toISOString(), days);

	return days;
}

export function unixToDate(seconds: number): Date {
	return new Date(seconds * 1000);
}

export function dateToUnix(date: Date): number {
	return parseInt((date.getTime() / 1000).toFixed());
}

export function picToPac(pic: PICResponse): PACResponse {
	const i = pic.snaps.map<Investment>((e) => {
		return {
			unix: e.unix,
			money: 0,
		};
	});

	return {
		index: 0,
		total: 0,
		simulationsWithSpots: [],
		annual: 0,
		absolute: 0,
		request: {
			...pic.request,
			monthFrequency: 1,
			initialInvestment: pic.request.initialInvestment,
			funds: pic.request.funds,
			rate: 0,
			differimentMounth: 0,
			days: [1],
			durationInvestment: 1,
			start: pic.snaps[0].unix,
			end: pic.snaps[pic.snaps.length - 1].unix,
			yearlyOnTopCommission: pic.request.annualCommission
		},
		snaps: pic.snaps,
	};
}

export function cutSnaps(snaps: Snap[], start?: Date, end?: Date): Snap[] {
	let startIndex = 0;
	let endIndex = snaps.length;

	if (start || end) {
		let story = snaps.map<string>((e) => myFormatDate(new Date(e.unix * 1000)));

		if (start) {
			startIndex = story.indexOf(myFormatDate(start));
			if (startIndex === -1) {
				throw new Error("non trovo lo start che mi hai indicato.");
			}
		}
		if (end) {
			endIndex = story.indexOf(myFormatDate(end));
			if (endIndex === -1) {
				throw new Error("non trovo end che mi hai indicato.");
			}
		}
		if (startIndex > endIndex) {
			throw new Error(
				"stai provando a tagliare la storia indicando la fine del taglio in data precedente all'inizio del taglio.",
			);
		}
	}

	return snaps.slice(startIndex, endIndex + 1);
}

export function getPercentage(start: number, end: number): number {
	return parseFloat(((start / end) * 100 - 100).toFixed(3));
}

export function annualEarge(ctu: number, cti: number, days: number): number {
	let a = ctu / cti;
	let y = days / 365;
	let g = 1 / (y === 0 ? 1 : y);
	if (isNaN(a)) a = 0;
	return Math.pow(a, g) - 1;
}

export function yearlyYield(
	amount: number,
	investedAmount: number,
	numberOfDays: number,
): number {
	return annualEarge(amount, investedAmount, numberOfDays);
}

export function absoluteEarge(ctu: number, cti: number): number {
	return ctu / cti - 1;
}

export function openJsonDownload(data: any) {
	let something = window.open(
		"data:text/json," + encodeURIComponent(JSON.stringify(data)),
		"_blank",
	);
	if (something) something.focus();
}

export function Int(n: number): number {
	return parseInt(n.toString());
}

export interface APIRequest<Vars = {}, Data = {}> {
	endpoint: APIEndpoint<Vars>;
	baseUrl?: string;
	headers?: Dictionary<string>;
	method?: "GET" | "POST" | "PUT" | "DELETE";
	dataKey?: string;
	legacy?: boolean;
	body?: Vars;
	json?: boolean;
}

type APIEndpoint<Vars> = string | ((vars: Vars) => string);

export async function runEmpirichQuery<T, Vars = {}>(
	request: APIRequest<Vars, T>,
): Promise<T> {
	let auther = authController().getAuther();
	let headers = request.headers ?? {};
	try {
		headers = Object.assign({}, headers, await auther.getHeaderToken());
	} catch { }

	return runRawEmpirichQuery({
		...request,
		baseUrl: request.baseUrl ?? config().URL,
		headers,
	});
}

export function useEmpirichQuery<T, Vars = {}>(
	request: APIRequest<Vars, T>,
	queryKey: QueryKey,
	options?: UseQueryOptions<T>,
) {
	return useQuery<T>(queryKey, async () => runEmpirichQuery(request), options);
}

export function useEmpirichMutation<T, Data = {}, Body = Data>(
	request: APIRequest<Data, Body>,
	queryKey: QueryKey,
	options?: UseQueryOptions<T> & {
		queriesToInvalidate?:
		| ((input: Data, response: T) => QueryKey[])
		| QueryKey[];
		shouldThrowError?: boolean;
	},
) {
	let auther = authController().getAuther();
	let getEndpoint = (data: Data) =>
		typeof request.endpoint === "string"
			? request.endpoint
			: request.endpoint(data);
	const queryClient = useQueryClient();
	return useMutation<T, {}, Data>(
		queryKey,
		async (data) => {
			let headers = {};
			try {
				headers = await auther.getHeaderToken();
			} catch { }
			// console.log("CALLING: ", config().URL + getEndpoint(data), "METHOD: ", request.method, "BODY: ", JSON.stringify(request.body ?? data) )
			return fetch(config().URL + getEndpoint(data), {
				headers,
				method: request.method ?? "POST",
				body:
					request.method !== "GET"
						? JSON.stringify(request.body ?? data)
						: null,
			})
				.then((res) => res.json())
				.then((res) => {
					if (res.hasError && options?.shouldThrowError) {
						throw new Error(res.error)
					}
					return request.dataKey != null ? res.payload[request.dataKey] : res.payload;
				},
				)
				.then((res) => {
					if (options?.queriesToInvalidate != null) {
						let queriesToInvalidate: QueryKey[] = [];
						if (typeof options.queriesToInvalidate === "function") {
							queriesToInvalidate = options.queriesToInvalidate(data, res);
						} else {
							queriesToInvalidate = options.queriesToInvalidate;
						}
						if (!isEmpty(queriesToInvalidate)) {
							// console.log("Invalidate", queriesToInvalidate);
							return Promise.all(
								queriesToInvalidate.map((d) =>
									queryClient.invalidateQueries(d),
								) ?? [],
							).then(() => res);
						}
					}

					return res;
				});
		},
		options as any,
	);
}

interface WindowSize {
	width: number;
	height: number;
}

export function useWindowSize(): WindowSize {
	const [windowSize, setWindowSize] = useState<WindowSize>({
		width: 0,
		height: 0,
	});

	const handleSize = () => {
		setWindowSize({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	useEventListener("resize", handleSize);

	// Set size at the first client-side load
	useIsomorphicLayoutEffect(() => {
		handleSize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return windowSize;
}

export interface ReportPatrimoniale {
	[sezione: string]: ParsedFund[];
}

const PDF_DATE_REGEX = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;


// Regex per validare ISIN (2 lettere + 10 alfanumerici)
const ISIN_REGEX = /^[A-Z]{2}[A-Z0-9]{10}$/;

// Regex per codice prodotto (da 3 a 5 caratteri alfanumerici)
const PRODUCT_CODE_REGEX = /^[A-Z0-9]{3,5}$/;

// La colonna J corrisponde all'indice 9 (A=0, B=1, C=2 ... J=9)
const COLONNA_CONTROVALORE_INDEX = 9;

/**
 * Pulisce e converte il valore della cella in un numero valido
 */
const parseControvalore = (val: any): number => {
	if (val === undefined || val === null || val === '') return 0;
	if (typeof val === 'number') return val;

	// Rimuove spazi e normalizza il formato numerico (es. 1.234,56 -> 1234.56)
	let cleanStr = val.toString().replace(/\s/g, '');
	if (cleanStr.includes('.') && cleanStr.includes(',')) {
		cleanStr = cleanStr.replace(/\./g, '').replace(',', '.');
	} else if (cleanStr.includes(',')) {
		cleanStr = cleanStr.replace(',', '.');
	}

	const num = parseFloat(cleanStr);
	return isNaN(num) ? 0 : num;
};

/**
 * Parser Excel per 5D
 */
export const parse5DExcel = async (file: File): Promise<ReportPatrimoniale> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target?.result as ArrayBuffer);
				const workbook = read(data, { type: 'array' });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];

				// Estrazione matrice row-by-row
				const rows = utils.sheet_to_json<any[]>(worksheet, { header: 1, defval: '' });

				const result: ReportPatrimoniale = {};
				let currentSection: string | null = null;
				// Se la riga successiva all'intestazione contiene "ISIN/PRODOTTO" nella seconda colonna l'isin sarà in colonna B (indice 1), altrimenti in colonna A (indice 0)
				let firstColumnIsin = false;

				for (let i = 0; i < rows.length; i++) {
					const row = rows[i];
					if (!row || row.length === 0) continue;

					// Prendiamo il valore della prima cella per intercettare i cambi di sezione
					const firstCell: string = (row[0] as string ?? "").toString().trim().toLowerCase();

					// 1. IDENTIFICAZIONE E SWITCH DELLA SEZIONE
					if (
						!isEmpty(firstCell) && (
							firstCell.includes('contratto:') ||
							firstCell.includes('- ruolo:') ||
							firstCell.includes('(polizza:')
						)
					) {
						currentSection = firstCell as string;
						if (result[currentSection] == null) {
							result[currentSection] = [];
						}
						continue;
					}
					// Se non abbiamo ancora trovato una sezione di interesse, saltiamo le righe
					if (!currentSection) continue;

					if (firstCell === "descrizione" || firstCell === "composizione") {
						if (((row[1] ?? "") as string).trim().toLowerCase() === "isin/prodotto") {
							firstColumnIsin = false;
						} else {
							firstColumnIsin = true;
						}
						continue;
					}

					// 2. RECUPERO DEL CODICE (ISIN o PRODOTTO) IN BASE AL COMPARTO
					const targetCodeIndex = firstColumnIsin ? 0 : 1;
					const codeToCheck = ((row[targetCodeIndex] ?? "") as string).toString().trim().toUpperCase();

					// 3. VALIDAZIONE REGEX E SCRITTURA DATI
					if (codeToCheck) {
						let isin = "";
						if (firstColumnIsin) {
							const values = codeToCheck.split(" ").filter(v => ISIN_REGEX.test(v))
							if (values.length > 0) isin = values[0];
						} else {
							if (ISIN_REGEX.test(codeToCheck) || PRODUCT_CODE_REGEX.test(codeToCheck) || codeToCheck in translateDictionary) isin = codeToCheck;
						}
						// Estraiamo il controvalore puntando direttamente all'indice della colonna J
						const rawControvalore = row[COLONNA_CONTROVALORE_INDEX];
						const controvalore = parseControvalore(rawControvalore);
						if (controvalore > 0 && !isEmpty(isin)) {
							result[currentSection].push({
								isin: translateMedCodeToIsin(isin),
								amount: controvalore,
								quote: 0
							});
						}
					}
				}
				// Clean-up: eliminiamo eventuali sezioni create ma rimaste vuote
				Object.keys(result).forEach(key => {
					if (result[key].length === 0) {
						delete result[key];
					}
				});
				resolve(result);
			} catch (error) {
				reject(new Error("Errore durante il parsing del file Excel: " + error));
			}
		};

		reader.onerror = () => reject(new Error("Errore di lettura del file."));
		reader.readAsArrayBuffer(file);
	});
};

export const getFundsIn5DSection = (funds: Fund[], section5D: ParsedFund[]): Fund[] => {
	return funds.map(
		(f) => {
			const index = section5D
				.findIndex(f5d =>
					// f5d.amount === f.amount &&
					(f5d.isin === f.isin || f5d.isin === f.fidaTicker)
				)
			if (index >= 0) {
				return { ...f, amount: section5D[index].amount }
			}
			return null
		}
	).filter(f => f != null)
}

export const get5DFundsCount = (formatted5D: ReportPatrimoniale, isin?: string, ticker?: string, key?: string) => {
	if (!isEmpty(isin) || !isEmpty(ticker)) {
		return Object.keys(formatted5D).reduce((p, n) =>
			p + formatted5D[n].filter(f => f.isin === isin || f.isin === ticker).length,
			0);
	} else if (!isEmpty(key)) {
		return formatted5D[key!] ? formatted5D[key!].length : 0;
	}
	return Object.keys(formatted5D).reduce((p, n) =>
		p + formatted5D[n].length,
		0);

}
export const removeToolFromReportPatrimoniale = (formatted5D: ReportPatrimoniale, isin?: string, ticker?: string, key?: string) => {
	// console.log("removeToolFromReportPatrimoniale: ", formatted5D, isin, ticker, key);
	let temp: ReportPatrimoniale = formatted5D;
	if (!isEmpty(isin) || !isEmpty(ticker)) {
		if (!isEmpty(key) && key) {
			if (Object.keys(formatted5D).includes(key)) {
				// Caso 1: rimuovo un fondo da una sezione
				const newKeySection = formatted5D[key!].filter(f5d => f5d.isin !== isin && f5d.isin !== ticker);
				temp = { ...temp, [key!]: newKeySection }
			}
		} else {
			// Caso 2: rimuovo un fondo da tutte le sezioni (missing isin)
			Object.keys(formatted5D).map(k => {
				temp = removeToolFromReportPatrimoniale(temp, isin, ticker, k);
			})
		}
	} else if (!isEmpty(key) && key) {
		// Caso 3: rimuovo una sezione
		const { [key]: _, ...rest } = temp;
		return rest;
	}
	// Clean-up: eliminiamo eventuali sezioni create ma rimaste vuote
	Object.keys(temp).forEach(key => {
		if (temp[key].length === 0) {
			delete temp[key];
		}
	});
	return temp;
}

export const toTitleCase = (str: string): string => {
	if (!str) return '';

	return str
		.trim()
		.toLowerCase()
		.replace(/\b\w/g, (char) => char.toUpperCase());
};


export const parse5DPDF = async (file: File): Promise<ReportPatrimoniale> => {

	return new Promise(async (resolve, reject) => {
		const result: ReportPatrimoniale = { "Dossier Titoli": [] }
		const tempResult: Map<string, number> = new Map();
		try {
			// Chiamata alla funzione di estrazione pura
			const pagineEstratte = await estraiTestoDaPdf(file, "#@#");
			pagineEstratte.map((pag, i) => {
				let temp = pag.toLowerCase();
				const tabStart = temp.indexOf("codice titolo");
				const tabEnd = temp.indexOf("totale controvalore");
				const tabPagEnd = temp.indexOf("posizione titoli");
				if (tabStart > 0 && (tabEnd > 0 || tabPagEnd > 0)) { // Formattazione corretta
					temp = temp.slice(
						tabStart,
						tabEnd >= 0 ? tabEnd : tabPagEnd
					).trim();
					const array = temp.split("#@#").filter(s => s.trim().length > 0);
					// console.log(`Pag.${i+1}: `, array);
					for (let i = 0; i < array.length; i++) {
						const cell = array[i].toUpperCase().trim();
						if (ISIN_REGEX.test(cell)) {
							const valuePositionFixer = PDF_DATE_REGEX.test(array[i + 4]) ? 6 : 7;
							tempResult.set(
								cell,
								tempResult.has(cell) ?
									(tempResult.get(cell) ?? 0) + parseControvalore(array[i + valuePositionFixer]) :
									parseControvalore(array[i + valuePositionFixer]));
						}
					}
				} else {
					reject(new Error("Formattazione del file sconosciuta"));
				}
			})
			// console.log("TEMP RESULT",tempResult);ß
			tempResult.keys().forEach(k => {
				result["Dossier Titoli"].push({
					isin: translateMedCodeToIsin(k),
					amount: tempResult.get(k) ?? 0,
					quote: 0
				})
			})
			resolve(result);
		} catch (error) {
			reject(new Error("Errore durante il parsing del file PDF: " + error));
		}

	});
};

export const get5DFundAmount = (formatted5D: ReportPatrimoniale, f: Fund) => {
	let amount = f.amount;
	const fundInstances: ParsedFund[] = [];
	Object.keys(formatted5D).forEach(key => {
		fundInstances.push(...formatted5D[key].filter(f5d => f5d.isin === f.isin || f5d.isin === f.fidaTicker));
	})
	if (fundInstances.length > 0) {
		amount = fundInstances.reduce((p, n) => p + n.amount, 0);
	}
	return amount;
}